// ============================================================
// Topbar — Top Navigation Bar
// ============================================================
// Search, school switcher (Owner), notifications, profile, theme toggle.

import { useState } from 'react';
import { AppBar, Toolbar, Box, IconButton, Badge, Menu, MenuItem, Typography, Divider, ListItemIcon, Tooltip } from '@mui/material';
import { Menu as MenuIcon, Bell, LogOut, User, Settings, ChevronDown, KeyRound, QrCode, ClipboardList, CalendarDays } from 'lucide-react';
import { FcLibrary } from 'react-icons/fc';
import { useNavigate } from 'react-router-dom';
import { tokens } from '@/theme/tokens';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import { useResponsive } from '@/hooks/useResponsive';
import { AppAvatar } from '@/components/layout/AppAvatar';
import { mockNotifications } from '@/mock/data';
import { SchoolSwitcher } from './SchoolSwitcher';
import { ResetPasswordModal } from './ResetPasswordModal';
import { AttendanceScannerModal } from '@/components/attendance/AttendanceScannerModal';
import { ROLES } from '@/constants/roles';
import useThemeStore from '@/store/themeStore';
import useUiStore from '@/store/uiStore';
import useAuthStore from '@/store/authStore';
import { isValidModule } from '@/utils/dynamicNavigation';
import { PATHS } from '@/router/paths';

export function Topbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentSchool } = useSchool();
  const modules = useAuthStore((s) => s.modules);
  const { isMobile, isMobileOrTablet } = useResponsive();
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useThemeStore((s) => s.toggleSidebar);
  const toggleMobileDrawer = useUiStore((s) => s.toggleMobileDrawer);

  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [scannerModalOpen, setScannerModalOpen] = useState(false);

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN || user?.role === 'super_admin' || user?.role === 'SUPER_ADMIN';
  const isOrgAdmin =
    user?.role === ROLES.ORGANIZATION_ADMIN ||
    user?.role === 'ORGANIZATION_ADMIN' ||
    user?.role === 'organization_admin';
  const isPrincipal =
    user?.role === ROLES.PRINCIPAL ||
    user?.role === 'PRINCIPAL' ||
    user?.role === 'principal';
  const isPrincipalOrOrgAdmin = isPrincipal || isOrgAdmin;

  const userMods = Array.isArray(modules) ? modules.filter(isValidModule) : [];
  const schoolMods = currentSchool?.school_modules || currentSchool?.modules || [];
  const mappedSchoolMods = Array.isArray(schoolMods)
    ? schoolMods.map((sm) => sm.module || sm).filter(isValidModule)
    : [];
  const activeMods = userMods.length > 0 ? userMods : mappedSchoolMods;

  const hasAttendanceModule = activeMods.some((mod) => {
    const modName = typeof mod === 'string' ? mod.toUpperCase() : (mod.name || mod.code || '').toUpperCase();
    return modName.includes('ATTENDANCE');
  });

  const settingsModule = activeMods.find((mod) => {
    const modData = mod.module || mod;
    const modName = typeof modData === 'string' ? modData.toUpperCase() : (modData.name || modData.id || '').toUpperCase();
    return modName === 'SETTINGS';
  });

  const qrScannerEnabled = useAuthStore((s) => s.qrScannerEnabled) ?? user?.qr_scanner_enabled ?? false;
  const showQrScanner = Boolean(qrScannerEnabled);
  const unreadCount = (mockNotifications || []).filter((n) => !n.read).length;

  const sidebarWidth = isMobileOrTablet
    ? 0
    : collapsed
      ? tokens.layout.sidebar.collapsedWidth
      : tokens.layout.sidebar.expandedWidth;

  const handleLogout = async () => {
    setProfileAnchor(null);
    await logout();
    navigate('/login');
  };

  const getHeaderTitle = () => {
    if (currentSchool) {
      const sName = currentSchool.name || currentSchool.school_name || 'School';
      return currentSchool.city ? `${sName} (${currentSchool.city})` : sName;
    }
    if (isSuperAdmin) {
      return 'Super Admin Portal';
    }
    if (isOrgAdmin) {
      return user?.organization_name || user?.organization?.name || 'Organization Portal';
    }
    return 'School Portal';
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          backgroundColor: tokens.colors.card,
          color: tokens.colors.text.primary,
          borderBottom: `1px solid ${tokens.colors.border}`,
          zIndex: tokens.zIndex.topbar,
        }}
      >
        <Toolbar
          sx={{
            height: tokens.layout.topbar.height,
            px: { xs: 2, sm: 3 },
            gap: 1,
          }}
        >
          {/* Sidebar Toggle (Desktop & Mobile) */}
          <IconButton
            onClick={isMobileOrTablet ? toggleMobileDrawer : toggleSidebar}
            edge="start"
          >
            <MenuIcon size={22} color={tokens.colors.text.primary} />
          </IconButton>

          {/* Current Selected Context Chip / School Switcher */}
          {!isMobile && (
            <SchoolSwitcher fallbackTitle={getHeaderTitle()} />
          )}

          <Box sx={{ flex: 1 }} />

          {/* Attendance QR Scanner Button */}
          {showQrScanner && (
            <Tooltip title="Scan Attendance QR">
              <IconButton
                size="small"
                onClick={() => setScannerModalOpen(true)}
                sx={{
                  bgcolor: `${tokens.colors.primary.main}15`,
                  color: tokens.colors.primary.main,
                  '&:hover': {
                    bgcolor: `${tokens.colors.primary.main}25`,
                  },
                }}
              >
                <QrCode size={20} color={tokens.colors.primary.main} />
              </IconButton>
            </Tooltip>
          )}



          {/* Notifications (Temporarily Hidden) */}
          {false && (
            <>
              <Tooltip title="Notifications">
                <IconButton
                  size="small"
                  onClick={(e) => setNotifAnchor(e.currentTarget)}
                >
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '10px',
                        height: 18,
                        minWidth: 18,
                      },
                    }}
                  >
                    <Bell size={20} color={tokens.colors.text.muted} />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Notification Menu */}
              <Menu
                anchorEl={notifAnchor}
                open={Boolean(notifAnchor)}
                onClose={() => setNotifAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  paper: {
                    sx: { width: 360, maxHeight: 400 },
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Notifications
                  </Typography>
                </Box>
                <Divider />
                {mockNotifications.map((notif) => (
                  <MenuItem
                    key={notif.id}
                    onClick={() => setNotifAnchor(null)}
                    sx={{
                      whiteSpace: 'normal',
                      py: 1.5,
                      backgroundColor: !notif.read ? tokens.colors.primary[50] + '40' : 'transparent',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={!notif.read ? 600 : 400}>
                        {notif.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notif.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: tokens.colors.text.muted, mt: 0.5 }}
                      >
                        {notif.time}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* User Profile */}
          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              ml: 1,
              px: 1,
              py: 0.5,
              borderRadius: tokens.radius.sm,
              '&:hover': { backgroundColor: tokens.colors.grey[100] },
            }}
          >
            <AppAvatar name={user?.name} size={34} />
            {!isMobile && (
              <Box sx={{ mr: 0.5 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ maxWidth: 120, lineHeight: 1.3 }}
                >
                  {user?.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ maxWidth: 120, textTransform: 'capitalize' }}
                >
                  {user?.role}
                </Typography>
              </Box>
            )}
            <ChevronDown size={16} color={tokens.colors.text.muted} />
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: { width: 260, mt: 1 },
              },
            }}
          >
            <MenuItem onClick={() => setProfileAnchor(null)}>
              <ListItemIcon>
                <User size={18} />
              </ListItemIcon>
              Profile
            </MenuItem>
            {!isSuperAdmin && (
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  setResetModalOpen(true);
                }}
              >
                <ListItemIcon>
                  <KeyRound size={18} />
                </ListItemIcon>
                Reset Password
              </MenuItem>
            )}
            {/* Settings (Only rendered if SETTINGS module is returned from backend) */}
            {settingsModule && (
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  navigate(PATHS.SETTINGS);
                }}
              >
                <ListItemIcon>
                  <Settings size={18} />
                </ListItemIcon>
                {settingsModule.display_name || 'Settings'}
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: tokens.colors.error.main }}>
              <ListItemIcon>
                <LogOut size={18} color={tokens.colors.error.main} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Reset Password Modal (non-super-admin users) */}
      {!isSuperAdmin && (
        <ResetPasswordModal
          open={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
        />
      )}

      {/* Attendance Scanner Modal */}
      {showQrScanner && (
        <AttendanceScannerModal
          open={scannerModalOpen}
          onClose={() => setScannerModalOpen(false)}
        />
      )}
    </>
  );
}
